import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Container,
  Eyebrow,
  SectionLabel,
  AccentButton,
  TextLink,
  CircleCTA,
  DiamondStat,
  Marquee,
  SurfaceCard,
  Pill,
} from "@/components/shared/Primitives";
import { RadarRings, CircleBadge } from "@/components/shared/Decorations";
import { BookCallButton, openCalendly } from "@/components/shared/BookCallButton";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { HOME_PILLARS, HOME_STATS, WORK_CASES, TESTIMONIALS, TICKER, tint } from "@/data/site";

const MockPanel = ({ from, to }) => (
  <div
    className="relative h-36 overflow-hidden rounded-[12px]"
    style={{ background: `linear-gradient(135deg, ${from}26, ${to}10)`, border: "1px solid var(--stroke-0)" }}
  >
    <div className="absolute inset-0 bg-grid opacity-40" />
    <div className="absolute left-4 top-4 flex gap-1.5">
      {[from, to, from].map((c, i) => (
        <span key={i} className="h-2 w-2 rounded-full" style={{ background: c, opacity: 0.75 }} />
      ))}
    </div>
    <div className="absolute bottom-4 left-4 right-4 flex items-end gap-1.5">
      {[34, 56, 44, 72, 52, 88, 64].map((h, i) => (
        <span key={i} className="flex-1 rounded-sm" style={{ height: `${h}px`, background: `linear-gradient(${to}, ${from})`, opacity: 0.55 }} />
      ))}
    </div>
  </div>
);

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-56 -top-44 h-[760px] w-[760px] opacity-70" style={{ color: "var(--page-accent)" }}>
          <RadarRings className="h-full w-full" />
        </div>
        <Container className="relative z-10 grid items-center gap-14 pb-24 pt-16 lg:grid-cols-[1.15fr_0.85fr] lg:pb-28 lg:pt-24">
          <div>
            <Reveal><Eyebrow>Studio — Agency · Apps · Lab</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h1
                className="font-display mt-7"
                style={{ fontSize: "clamp(52px, 8.4vw, 124px)", lineHeight: 0.9, letterSpacing: "0.02em", color: "var(--text-0)" }}
              >
                A STUDIO FOR<br />BRANDS THAT<br />REFUSE TO<br />
                <span className="glow-accent" style={{ color: "var(--page-accent)" }}>BLEND IN.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 max-w-md text-[15px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>
                Emailink is a retention agency, a product studio and a lab — building the systems,
                apps and experiments that turn an audience into revenue.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <BookCallButton data-testid="home-hero-cta">Book a call</BookCallButton>
                <AccentButton to="/work" variant="outline">See the work</AccentButton>
              </div>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-12 flex flex-wrap gap-x-10 gap-y-5">
                {HOME_STATS.map((s) => (
                  <div key={s.label}>
                    <p className="font-display tabular text-3xl leading-none" style={{ color: "var(--text-0)" }}>{s.value}</p>
                    <p className="font-tech mt-1.5 text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="relative flex flex-col items-center gap-9">
              <div className="w-[260px] max-w-full animate-float sm:w-[300px]" style={{ color: "var(--page-accent)" }}>
                <CircleBadge value="$24M+" label="GENERATED" className="h-full w-full" />
              </div>
              <div className="flex gap-9">
                <DiamondStat value="41%" label="AVG SHARE" />
                <DiamondStat value="90D" label="SPRINT" warm />
              </div>
            </div>
          </Reveal>
        </Container>

        <div className="border-y py-4" style={{ borderColor: "var(--stroke-0)", background: "var(--strip-bg)" }}>
          <Marquee items={TICKER} />
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 lg:py-24">
        <Container>
          <SectionLabel index="01" total="03">What we do</SectionLabel>
          <Reveal>
            <h2 className="mb-12 max-w-2xl text-2xl font-semibold leading-tight sm:text-3xl" style={{ color: "var(--text-0)" }}>
              Three ways we work — done-for-you, do-it-yourself, and the weird stuff in between.
            </h2>
          </Reveal>
          <Stagger className="grid gap-5 md:grid-cols-3">
            {HOME_PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <StaggerItem key={p.id}>
                  <Link to={p.to} className="group block h-full" data-testid={`home-pillar-${p.id}`}>
                    <SurfaceCard className="flex h-full flex-col">
                      <div className="mb-6 flex items-center justify-between">
                        <span
                          className="clip-hex grid h-12 w-12 place-items-center"
                          style={{ background: tint(14, p.accent), color: p.accent }}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="font-display text-2xl" style={{ color: tint(70, p.accent) }}>{p.index}</span>
                      </div>
                      <h3 className="text-xl font-semibold" style={{ color: "var(--text-0)" }}>{p.title}</h3>
                      <p className="mt-3 flex-1 text-[14px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>{p.blurb}</p>
                      <div className="mt-6 flex flex-wrap items-center gap-2">
                        {p.tags.map((t) => <Pill key={t}>{t}</Pill>)}
                      </div>
                      <div className="mt-6 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.1em] transition-transform group-hover:translate-x-1" style={{ color: p.accent }}>
                        Explore <ArrowUpRight className="h-4 w-4" />
                      </div>
                    </SurfaceCard>
                  </Link>
                </StaggerItem>
              );
            })}
          </Stagger>
        </Container>
      </section>

      {/* FEATURED WORK */}
      <section className="py-20 lg:py-24">
        <Container>
          <SectionLabel index="02" total="03">Selected work</SectionLabel>
          <Stagger className="grid gap-5 md:grid-cols-3">
            {WORK_CASES.slice(0, 3).map((c) => (
              <StaggerItem key={c.brand}>
                <SurfaceCard className="flex h-full flex-col">
                  <MockPanel from={c.accentFrom} to={c.accentTo} />
                  <div className="mt-5 flex items-center justify-between">
                    <h3 className="text-lg font-semibold" style={{ color: "var(--text-0)" }}>{c.brand}</h3>
                    <span className="font-tech text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{c.category}</span>
                  </div>
                  <div className="mt-4">
                    <p className="font-display text-3xl" style={{ color: c.accentFrom }}>{c.metric}</p>
                    <p className="font-tech text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{c.metricLabel}</p>
                  </div>
                  <p className="mt-4 flex-1 text-[13px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>{c.blurb}</p>
                </SurfaceCard>
              </StaggerItem>
            ))}
          </Stagger>
          <div className="mt-10"><TextLink to="/work">See all case studies</TextLink></div>
        </Container>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-24">
        <Container>
          <SectionLabel index="03" total="03">Client signal</SectionLabel>
          <div className="grid gap-5 lg:grid-cols-[1.25fr_1fr]">
            <Reveal>
              <div
                className="relative flex h-full flex-col justify-center overflow-hidden rounded-[22px] p-9 sm:p-12"
                style={{ background: tint(6), border: `1px solid ${tint(22)}` }}
              >
                <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 opacity-60" style={{ color: "var(--page-accent)" }}>
                  <RadarRings className="h-full w-full" />
                </div>
                <span className="font-tech text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--page-accent)" }}>Client perspective</span>
                <blockquote className="relative z-10 mt-6 text-xl font-light leading-relaxed sm:text-2xl" style={{ color: "var(--text-1)" }}>
                  “{TESTIMONIALS[0].quote}”
                </blockquote>
                <div className="mt-8 h-px w-10" style={{ background: tint(40) }} />
                <p className="mt-4 text-[14px] font-medium" style={{ color: "var(--text-0)" }}>{TESTIMONIALS[0].name}</p>
                <p className="font-tech text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{TESTIMONIALS[0].role}</p>
              </div>
            </Reveal>
            <Stagger className="grid gap-5">
              {TESTIMONIALS.slice(1).map((t) => (
                <StaggerItem key={t.name}>
                  <SurfaceCard hover={false} className="h-full">
                    <blockquote className="text-[15px] font-light leading-relaxed" style={{ color: "var(--text-1)" }}>“{t.quote}”</blockquote>
                    <p className="mt-4 text-[13px] font-medium" style={{ color: "var(--text-0)" }}>{t.name}</p>
                    <p className="font-tech text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{t.role}</p>
                  </SurfaceCard>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-24 lg:py-28">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(700px 360px at 30% 120%, rgba(255,107,43,0.12), transparent 60%)" }} />
        <Container className="relative z-10 grid items-end gap-12 lg:grid-cols-[1fr_auto]">
          <div>
            <Eyebrow>Start here</Eyebrow>
            <h2 className="font-display mt-6" style={{ fontSize: "clamp(44px, 6.4vw, 96px)", lineHeight: 0.95, letterSpacing: "0.02em", color: "var(--text-0)" }}>
              FIND OUT WHAT<br />YOUR LIST IS<br />ACTUALLY WORTH.
            </h2>
          </div>
          <CircleCTA onClick={openCalendly} sub="30 min · no pitch · just work" />
        </Container>
      </section>
    </div>
  );
}
